import { useRef } from 'react'
import { Link, useParams } from 'react-router-dom'
import BombTimerLegalLanguageSelector from '../components/BombTimerLegalLanguageSelector'
import PageHero from '../components/PageHero'
import PageSeo from '../components/PageSeo'
import SiteFooter from '../components/SiteFooter'
import TopNav from '../components/TopNav'
import {
  getBombTimerLegalLocale,
  getBombTimerLegalPath,
  getBombTimerLegalTranslation,
  useBombTimerLegalTranslation,
} from '../lib/bombTimerLegal'
import { buildOrganizationSchema, getAbsoluteUrl } from '../lib/seo'
import './ArticlePage.css'

const effectiveDate = '2026-09-28'
export default function BombTimerPrivacyPolicyPage() {
  const { locale: localeParam } = useParams<{ locale: string }>()
  const locale = getBombTimerLegalLocale(localeParam)
  const rootRef = useRef<HTMLDivElement>(null)
  const localizedPolicyPath = getBombTimerLegalPath('privacy-policy', locale)
  const localizedTermsPath = getBombTimerLegalPath('terms-and-conditions', locale)
  const translate = (source: string) => getBombTimerLegalTranslation(locale, source)
  useBombTimerLegalTranslation(rootRef, locale)

  return (
    <div
      key={locale}
      ref={rootRef}
      className="article-page"
      dir={locale === 'ar' ? 'rtl' : 'ltr'}
      lang={locale}
    >
      <PageSeo
        title={translate('Privacy Policy | Bomb Timer')}
        description={translate(
          'Read the Bomb Timer privacy policy, including local app data, purchases, RevenueCat, retention, and privacy rights.',
        )}
        canonical={getAbsoluteUrl(localizedPolicyPath)}
        image={getAbsoluteUrl('/images/bomb-timer-logo.png')}
        keywords={['bomb timer privacy policy', 'bomb timer app privacy', 'privacy policy']}
        schema={[buildOrganizationSchema()]}
      />

      <TopNav />

      <main>
        <article className="article-shell">
          <PageHero as="header" innerClassName="article-hero__inner">
            <nav className="article-breadcrumbs" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <span>Bomb Timer</span>
              <span>/</span>
              <span>Privacy Policy</span>
            </nav>

            <p className="eyebrow eyebrow--blog">Bomb Timer · Legal</p>
            <h1 className="article-hero__title heading-light">Privacy Policy</h1>
            <p className="article-hero__description p-intro">
              This Privacy Policy applies to the Bomb Timer mobile application.
            </p>

            <div className="article-hero__meta">
              <span>Effective date</span>
              <span>{effectiveDate}</span>
            </div>

            <BombTimerLegalLanguageSelector document="privacy-policy" locale={locale} />
          </PageHero>

          <div className="container article-body-wrap">
            <div className="article-layout article-layout--single">
              <div className="article-content article-content--legal">
                <p className="article-intro">
                  This Privacy Policy explains how Hatcher trading as Contrast Shower ("we", "us" or
                  "our"), an individual/sole trader based in Queensland, Australia, handles
                  information in connection with the Bomb Timer mobile application ("Bomb Timer" or
                  the "Application"). Bomb Timer&apos;s bundle identifier and Android package name
                  are com.bombtimer.app.
                </p>

                <section className="article-section">
                  <h2>1. Privacy at a Glance</h2>
                  <div className="article-list-block">
                    <ul>
                      <li>
                        Bomb Timer does not require an account or ask for your name or email
                        address.
                      </li>
                      <li>
                        We do not include advertising, third-party analytics or cross-app tracking
                        SDKs.
                      </li>
                      <li>
                        We do not request or collect precise location, contacts, photos or
                        microphone recordings.
                      </li>
                      <li>
                        Your selected language and daily free-use count are stored locally on your
                        device.
                      </li>
                      <li>
                        RevenueCat and your app store process information needed for purchases and
                        premium access.
                      </li>
                    </ul>
                  </div>
                </section>

                <section className="article-section">
                  <h2>2. Information Handled by Bomb Timer</h2>
                  <h3>2.1 Information Stored on Your Device</h3>
                  <p>
                    Bomb Timer stores your selected language and a dated count of free timer uses on
                    your device. This information is used to remember your preferences and apply the
                    daily free-use allowance. We do not transmit this local information to our own
                    servers. You can remove it by clearing the Application&apos;s data or
                    uninstalling the Application, subject to your device&apos;s behaviour and backup
                    settings.
                  </p>

                  <h3>2.2 Purchases and Premium Access</h3>
                  <p>
                    Bomb Timer uses RevenueCat to offer purchases, verify transactions, restore
                    purchases and determine whether premium access is active. RevenueCat generates
                    an anonymous Application user identifier because Bomb Timer does not have its
                    own account system. RevenueCat may process transaction or receipt information,
                    product identifiers, purchase history, subscription and entitlement status,
                    store and platform information, and technical information needed to provide and
                    protect its service.
                  </p>
                  <p>
                    Payments are processed by Apple through the App Store or by Google through
                    Google Play. We do not receive or store your full payment-card or bank-account
                    details. Apple, Google and RevenueCat handle information under their own privacy
                    policies.
                  </p>

                  <h3>2.3 Support Communications</h3>
                  <p>
                    If you contact us, we receive the information you choose to provide, such as
                    your email address, message and any diagnostic details or screenshots you
                    include. We use that information to respond, investigate issues and maintain
                    appropriate support records.
                  </p>
                </section>

                <section className="article-section">
                  <h2>3. How We Use Information</h2>
                  <p>We and our service providers use information described above to:</p>
                  <div className="article-list-block">
                    <ul>
                      <li>provide the Application and remember local preferences;</li>
                      <li>process, verify and restore purchases;</li>
                      <li>grant and maintain premium entitlements;</li>
                      <li>prevent purchase fraud and troubleshoot technical or support issues;</li>
                      <li>comply with legal, accounting and app-store obligations; and</li>
                      <li>protect our rights, users and services.</li>
                    </ul>
                  </div>
                  <p>
                    We do not use Bomb Timer data for third-party advertising, sell personal
                    information, or use it to track you across apps or websites for advertising.
                  </p>
                </section>

                <section className="article-section">
                  <h2>4. Service Providers and Disclosure</h2>
                  <p>Information may be handled by or disclosed to:</p>
                  <div className="article-list-block">
                    <ul>
                      <li>
                        RevenueCat, which provides purchase validation and entitlement management;
                      </li>
                      <li>
                        Apple and Google, which distribute the Application and process payments;
                      </li>
                      <li>
                        professional advisers or authorities where reasonably necessary to comply
                        with law, resolve disputes, prevent fraud or protect legal rights; and
                      </li>
                      <li>
                        a successor if our business or the Application is reorganised, sold or
                        transferred, subject to applicable law.
                      </li>
                    </ul>
                  </div>
                  <p>
                    These providers may process information in Australia, the United States and
                    other countries where they or their subprocessors operate. Privacy protections
                    in those countries may differ from those in your country.
                  </p>
                </section>

                <section className="article-section">
                  <h2>5. Retention and Deletion</h2>
                  <p>
                    Local preferences and the free-use count remain on your device until
                    overwritten, cleared or removed with the Application, subject to device backups.
                    Support correspondence is retained only as long as reasonably necessary to
                    resolve the request, maintain appropriate records and meet legal obligations.
                  </p>
                  <p>
                    RevenueCat, Apple and Google retain purchase and transaction records according
                    to their own policies and legal obligations. You may contact us to request
                    access, correction or deletion of information we control. Because Bomb Timer
                    uses an anonymous RevenueCat identifier and has no account, we may need
                    information from your device or purchase record to locate relevant data, and in
                    some cases we may not be able to associate an anonymous record with you.
                    App-store transaction records may need to be retained by the applicable store.
                  </p>
                </section>

                <section className="article-section">
                  <h2>6. Your Choices and Rights</h2>
                  <p>
                    Depending on where you live, you may have rights to request access to,
                    correction of or deletion of your personal information, or to object to or
                    restrict certain processing. You may also complain to your local privacy
                    regulator. Contact us to exercise a right or make a privacy complaint. We will
                    respond in accordance with applicable law.
                  </p>
                  <p>
                    You can manage subscriptions through your Apple App Store or Google Play
                    account. Bomb Timer also provides purchase restoration and
                    subscription-management options.
                  </p>
                </section>

                <section className="article-section">
                  <h2>7. Children</h2>
                  <p>
                    Bomb Timer is a general-audience application and is not designed to collect
                    personal information directly from children. The Application does not provide
                    accounts, social features or targeted advertising. Purchases by a minor must be
                    authorised and managed by a parent or legal guardian as required by the
                    applicable app store and local law. If you believe a child has provided personal
                    information to us through a support request, please contact us so we can take
                    appropriate action.
                  </p>
                </section>

                <section className="article-section">
                  <h2>8. Security</h2>
                  <p>
                    We take reasonable technical and organisational measures to protect information
                    we control. No method of electronic transmission or storage is completely
                    secure, so we cannot guarantee absolute security. Keep your device and
                    Application updated and protect access to your app-store account.
                  </p>
                </section>

                <section className="article-section">
                  <h2>9. Third-Party Policies</h2>
                  <div className="article-list-block">
                    <ul>
                      <li>
                        <a href="https://www.revenuecat.com/privacy/" rel="noreferrer">
                          RevenueCat Privacy Policy
                        </a>
                      </li>
                      <li>
                        <a href="https://www.apple.com/legal/privacy/" rel="noreferrer">
                          Apple Privacy Policy
                        </a>
                      </li>
                      <li>
                        <a href="https://policies.google.com/privacy" rel="noreferrer">
                          Google Privacy Policy
                        </a>
                      </li>
                    </ul>
                  </div>
                  <p>
                    Links open websites we do not control. Their privacy practices are governed by
                    their own policies.
                  </p>
                </section>

                <section className="article-section">
                  <h2>10. Changes to This Policy</h2>
                  <p>
                    We may update this Privacy Policy when the Application, our providers or legal
                    requirements change. We will publish the revised policy on this page and update
                    the effective date. Material changes may also be communicated within the
                    Application where reasonably appropriate.
                  </p>
                </section>

                <section className="article-section">
                  <h2>11. Contact Us</h2>
                  <p>
                    For privacy questions, requests or complaints, contact Hatcher trading as
                    Contrast Shower at{' '}
                    <a href="mailto:support@contrastshower.com">support@contrastshower.com</a>. You
                    can also review the{' '}
                    <Link to={localizedTermsPath}>
                      Bomb Timer Terms and Conditions
                    </Link>
                    .
                  </p>
                </section>
              </div>
            </div>
          </div>
        </article>
      </main>

      <SiteFooter />
    </div>
  )
}
